import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import chatService from "../../services/chatService";
import ChatCardRenderer from "./ChatCardRenderer";
import ChatMessageContent from "./ChatMessageContent";
import ChatThinkingIndicator from "./ChatThinkingIndicator";
import ChatHistorySidebar from "./ChatHistorySidebar";
import { API_CONFIG } from "../../utils/apiConfig";

const INITIAL_SUGGESTIONS = [
  "What is the live stock summary across all categories?",
  "Which shipments are arriving this week?",
  "Are there any containers with demurrage risks?",
  "Show overdue receivables for customers",
  "Show recent purchase bills and suppliers",
  "Check total inventory weight in Yard 1",
  "Show top customers with unpaid invoices",
];

export default function ChatDrawer({ isOpen, onClose }) {
  const [activeSessionId, setActiveSessionId] = useState(() => {
    try {
      return (
        sessionStorage.getItem("rai_chat_active_session_id") ||
        `conv-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`
      );
    } catch (_) {
      return `conv-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    }
  });

  const [sessions, setSessions] = useState(() => {
    try {
      const cached = localStorage.getItem("rai_chat_sessions_cache");
      return cached ? JSON.parse(cached) : [];
    } catch (_) {
      return [];
    }
  });

  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const [messages, setMessages] = useState(() => {
    try {
      const saved = sessionStorage.getItem("rai_chat_history");
      return saved ? JSON.parse(saved) : [];
    } catch (_) {
      return [];
    }
  });

  const [input, setInput] = useState("");
  const [currentQuery, setCurrentQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [suggestedPrompts, setSuggestedPrompts] = useState(INITIAL_SUGGESTIONS);

  const [isFullscreen, setIsFullscreen] = useState(() => {
    try {
      return sessionStorage.getItem("rai_chat_fullscreen") === "true";
    } catch (_) {
      return false;
    }
  });

  const [isHistoryOpen, setIsHistoryOpen] = useState(() => {
    try {
      const saved = sessionStorage.getItem("rai_chat_history_open");
      return saved !== null ? saved === "true" : true; // default open in fullscreen
    } catch (_) {
      return true;
    }
  });

  const location = useLocation();
  const prevLocationRef = useRef(location.pathname + location.search);
  const messagesEndRef = useRef(null);
  const chatBodyRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);
  const suggestionsContainerRef = useRef(null);
  const prevMessagesLengthRef = useRef(messages.length);

  const savedScrollTopRef = useRef(() => {
    try {
      const saved = sessionStorage.getItem("rai_chat_scroll_top");
      return saved !== null ? Number(saved) : null;
    } catch (_) {
      return null;
    }
  });

  // Auto-resize input textarea to smoothly fit up to 5 lines of text before vertical scrolling appears
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";

    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight) || 20;
    const paddingTop = parseFloat(style.paddingTop) || 4;
    const paddingBottom = parseFloat(style.paddingBottom) || 4;

    const singleLineHeight = Math.ceil(lineHeight + paddingTop + paddingBottom);
    const maxLines = 5;
    const maxHeight = Math.ceil(lineHeight * maxLines + paddingTop + paddingBottom + 2);

    const scrollHeight = textarea.scrollHeight;

    if (scrollHeight > maxHeight + 2) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = "auto";
    } else {
      textarea.style.height = `${Math.max(scrollHeight, singleLineHeight)}px`;
      textarea.style.overflowY = "hidden";
    }
  }, []);

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem("rai_chat_fullscreen", String(next));
      } catch (_) { }
      return next;
    });
  };

  const toggleHistory = () => {
    setIsHistoryOpen((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem("rai_chat_history_open", String(next));
      } catch (_) { }
      return next;
    });
  };

  // Fetch past sessions from backend
  const fetchSessions = useCallback(async () => {
    setIsHistoryLoading(true);
    try {
      const data = await chatService.getSessions();
      if (Array.isArray(data)) {
        setSessions(data);
        try {
          localStorage.setItem("rai_chat_sessions_cache", JSON.stringify(data));
        } catch (_) { }
      }
    } catch (err) {
      console.warn("Could not load sessions from API, keeping cached:", err);
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);

  // Keyboard shortcut listener: Ctrl+N (New Chat), Ctrl+H (Toggle History)
  useEffect(() => {
    const handleKeyDownGlobal = (e) => {
      if (!isOpen) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n") {
        e.preventDefault();
        handleNewChat();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "h") {
        e.preventDefault();
        toggleHistory();
      }
    };

    window.addEventListener("keydown", handleKeyDownGlobal);
    return () => window.removeEventListener("keydown", handleKeyDownGlobal);
  }, [isOpen]);

  useEffect(() => {
    adjustTextareaHeight();
    const t = setTimeout(adjustTextareaHeight, 150);
    return () => clearTimeout(t);
  }, [isFullscreen, adjustTextareaHeight]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, isOpen, adjustTextareaHeight]);

  useEffect(() => {
    const handleResize = () => adjustTextareaHeight();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [adjustTextareaHeight]);

  // Automatically close chat drawer whenever route changes
  useEffect(() => {
    const currentLoc = location.pathname + location.search;
    if (prevLocationRef.current !== currentLoc) {
      prevLocationRef.current = currentLoc;
      if (isOpen) {
        onClose();
      }
    }
  }, [location.pathname, location.search, isOpen, onClose]);

  const scrollToBottom = (behavior = "auto") => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior,
      });
    }
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  };

  const handleScroll = (e) => {
    if (e.target) {
      savedScrollTopRef.current = e.target.scrollTop;
      try {
        sessionStorage.setItem("rai_chat_scroll_top", String(e.target.scrollTop));
      } catch (_) { }
    }
  };

  // Restore exact position where user was when re-opening the chat drawer
  useEffect(() => {
    if (isOpen) {
      chatService
        .getStatus()
        .then((res) => setStatus(res))
        .catch(() => setStatus({ isConfigured: false }));

      fetchSessions();

      const focusTimer = setTimeout(() => {
        textareaRef.current?.focus();
        adjustTextareaHeight();
      }, 150);

      const restoreScrollPosition = () => {
        if (!chatBodyRef.current) return;
        let pos = typeof savedScrollTopRef.current === "function"
          ? savedScrollTopRef.current()
          : savedScrollTopRef.current;

        if (pos === null) {
          try {
            const stored = sessionStorage.getItem("rai_chat_scroll_top");
            if (stored !== null) pos = Number(stored);
          } catch (_) { }
        }

        if (pos !== null && !isNaN(pos)) {
          chatBodyRef.current.scrollTop = pos;
        } else {
          chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
      };

      restoreScrollPosition();
      const t1 = setTimeout(restoreScrollPosition, 40);
      const t2 = setTimeout(restoreScrollPosition, 120);
      return () => {
        clearTimeout(focusTimer);
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [isOpen, adjustTextareaHeight, fetchSessions]);

  // Auto-scroll when new message is submitted or streaming
  useEffect(() => {
    try {
      sessionStorage.setItem("rai_chat_history", JSON.stringify(messages));
      sessionStorage.setItem("rai_chat_active_session_id", activeSessionId);
    } catch (_) { }

    if (messages.length > prevMessagesLengthRef.current || isLoading) {
      scrollToBottom("smooth");
      setTimeout(() => {
        if (chatBodyRef.current) {
          savedScrollTopRef.current = chatBodyRef.current.scrollTop;
          try {
            sessionStorage.setItem("rai_chat_scroll_top", String(chatBodyRef.current.scrollTop));
          } catch (_) { }
        }
      }, 150);
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages, isLoading, activeSessionId]);

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  /**
   * Start a brand new empty chat session
   */
  const handleNewChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);

    const newId = `conv-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    setActiveSessionId(newId);
    setMessages([]);
    setSuggestedPrompts(INITIAL_SUGGESTIONS);
    setInput("");
    savedScrollTopRef.current = 0;

    try {
      sessionStorage.setItem("rai_chat_active_session_id", newId);
      sessionStorage.removeItem("rai_chat_history");
      sessionStorage.removeItem("rai_chat_scroll_top");
    } catch (_) { }

    setTimeout(() => {
      textareaRef.current?.focus();
      adjustTextareaHeight();
    }, 100);
  };

  /**
   * Load historical turns from a selected chat session
   */
  const handleSelectSession = async (session) => {
    if (!session || session.sessionId === activeSessionId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    setActiveSessionId(session.sessionId);

    try {
      sessionStorage.setItem("rai_chat_active_session_id", session.sessionId);
      const turns = await chatService.getSessionMessages(session.sessionId);
      if (Array.isArray(turns) && turns.length > 0) {
        setMessages(turns);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error("Failed to load session messages:", err);
    }
  };

  /**
   * Delete single conversation session
   */
  const handleDeleteSession = async (sessionId) => {
    try {
      await chatService.deleteSession(sessionId);
      setSessions((prev) => {
        const next = prev.filter((s) => s.sessionId !== sessionId);
        try {
          localStorage.setItem("rai_chat_sessions_cache", JSON.stringify(next));
        } catch (_) { }
        return next;
      });

      if (sessionId === activeSessionId) {
        handleNewChat();
      }
    } catch (err) {
      console.error("Failed to delete session:", err);
    }
  };

  /**
   * Bulk delete all conversations from a specific date
   */
  const handleDeleteDate = async (dateKey) => {
    if (!dateKey) return;
    try {
      await chatService.deleteSessionsByDate(dateKey);
      setSessions((prev) => {
        const next = prev.filter((s) => s.dateKey !== dateKey);
        try {
          localStorage.setItem("rai_chat_sessions_cache", JSON.stringify(next));
        } catch (_) { }
        return next;
      });

      const currentSession = sessions.find((s) => s.sessionId === activeSessionId);
      if (currentSession && currentSession.dateKey === dateKey) {
        handleNewChat();
      }
    } catch (err) {
      console.error("Failed to delete sessions by date:", err);
    }
  };

  /**
   * Clear all conversation history for the user
   */
  const handleClearAllSessions = async () => {
    try {
      await chatService.clearAllSessions();
      setSessions([]);
      try {
        localStorage.removeItem("rai_chat_sessions_cache");
      } catch (_) { }
      handleNewChat();
    } catch (err) {
      console.error("Failed to clear all sessions:", err);
    }
  };

  const handleSend = async (textToSend) => {
    const query = (textToSend || input).trim();
    if (!query) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const userMsg = { role: "user", content: query, timestamp: new Date().toISOString() };
    const priorHistory = messages
      .filter((m) => !m.isError && !m.isStopped && m.content)
      .slice(-4)
      .map((m) => ({
        role: m.role,
        content: m.content,
      }));

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setCurrentQuery(query);
    setIsLoading(true);

    try {
      const response = await chatService.sendMessage({
        message: query,
        history: priorHistory,
        conversationId: activeSessionId,
        signal: abortController.signal,
      });

      const assistantMsg = {
        role: "assistant",
        content: response?.reply || "No response received.",
        structuredContent: response?.structuredContent || null,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMsg]);
      if (response?.suggestedFollowUps && response.suggestedFollowUps.length > 0) {
        setSuggestedPrompts(response.suggestedFollowUps);
      }

      // Update session list optimistically
      const returnedId = response?.conversationId || activeSessionId;
      if (returnedId !== activeSessionId) {
        setActiveSessionId(returnedId);
      }

      setSessions((prev) => {
        let title = query.trim().replace(/[\r\n]+/g, " ");
        if (title.length > 55) title = title.substring(0, 52) + "...";

        const exists = prev.some((s) => s.sessionId === returnedId);
        let next;
        if (!exists) {
          const newSession = {
            sessionId: returnedId,
            title,
            dateKey: new Date().toISOString().slice(0, 10),
            createdDate: new Date().toISOString(),
            modifiedDate: new Date().toISOString(),
            messageCount: 2,
          };
          next = [newSession, ...prev];
        } else {
          next = prev.map((s) =>
            s.sessionId === returnedId
              ? { ...s, modifiedDate: new Date().toISOString(), messageCount: (s.messageCount || 0) + 2 }
              : s
          );
        }

        try {
          localStorage.setItem("rai_chat_sessions_cache", JSON.stringify(next));
        } catch (_) { }
        return next;
      });
    } catch (err) {
      const isAborted =
        err?.name === "CanceledError" ||
        err?.code === "ERR_CANCELED" ||
        err?.message === "canceled" ||
        abortController.signal.aborted;

      if (isAborted) {
        const stoppedMsg = {
          role: "assistant",
          content: "Generation stopped. You can enter another prompt.",
          isStopped: true,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, stoppedMsg]);
        return;
      }

      const rawMessage = String(err?.message || "").trim();
      const isTechnicalError =
        /nvidia|nim|api error|500|completions|failed to fetch|axios/i.test(rawMessage) ||
        !rawMessage;
      const userMessage = isTechnicalError
        ? "Something went wrong, please try again."
        : rawMessage;

      const errorMsg = {
        role: "assistant",
        content: userMessage,
        isError: true,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
        setIsLoading(false);
      }
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className={`fixed z-50 flex flex-col bg-white shadow-2xl transition-all duration-300 ease-in-out dark:bg-slate-900 ${isFullscreen
        ? "inset-0 w-full h-full"
        : "inset-y-0 right-0 w-full max-w-lg border-l border-slate-200 dark:border-slate-800"
        } ${isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"}`}
      aria-hidden={!isOpen}
    >
      {/* Top Header */}
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md sticky top-0 z-20 shrink-0">
        <div
          className={`flex items-center justify-between transition-all ${isFullscreen ? "w-full px-6 py-3" : "w-full px-4 py-3"
            }`}
        >
          {/* Header Left: History Toggle, New Chat Quick Button & Brand */}
          <div className="flex items-center gap-2 sm:gap-2.5">
            {/* History Toggle Button */}
            <button
              type="button"
              onClick={toggleHistory}
              title={isHistoryOpen ? "Hide chat history (Ctrl+H)" : "View chat history (Ctrl+H)"}
              aria-label="Toggle chat history"
              className={`flex h-8 w-8 items-center justify-center rounded-lg transition cursor-pointer ${isHistoryOpen
                ? "bg-primary-100 text-primary-700 dark:bg-primary-950/60 dark:text-primary-300 ring-1 ring-primary-500/30"
                : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 dark:text-slate-400"
                }`}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>

            {/* Quick + New Chat Button */}
            <button
              type="button"
              onClick={handleNewChat}
              title="New Chat (Ctrl+N)"
              aria-label="New Chat"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-primary-600 dark:text-primary-400 hover:border-primary-500 hover:bg-primary-50/60 dark:hover:bg-primary-950/40 transition cursor-pointer active:scale-95 shadow-2xs"
            >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
            </button>

            <div className="flex items-center gap-2 ml-1">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-tr from-primary-600 via-primary-500 to-amber-500 text-white shadow-md shadow-primary-500/25 shrink-0">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="hidden xs:block">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate max-w-[130px] sm:max-w-[200px]">
                    AI Assistant
                  </h3>
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${status?.isConfigured ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
                      }`}
                    title={status?.isConfigured ? "Online" : "Connecting..."}
                  />
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 hidden sm:block">
                  Verified Double-Entry Ledger Intelligence
                </p>
              </div>
            </div>
          </div>

          {/* Header Right: Clear Thread, Fullscreen Toggle & Close Button */}
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                type="button"
                onClick={handleNewChat}
                title="Clear Active Chat"
                aria-label="Clear Active Chat"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 cursor-pointer"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}

            {/* Fullscreen / Restore Toggle Button */}
            <button
              type="button"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Restore to sidebar drawer" : "Expand to full screen"}
              aria-label={isFullscreen ? "Restore to sidebar drawer" : "Expand to full screen"}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 cursor-pointer"
            >
              {isFullscreen ? (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 14h6m0 0v6m0-6L3 21m17-7h-6m0 0V4m0 6l7-7" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 3h6m0 0v6m0-6L14 10M9 21H3m0 0v-6m0 6l7-7" />
                </svg>
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              title="Close Assistant"
              aria-label="Close Assistant"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 cursor-pointer"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Container: Sidebar + Conversation Body */}
      <div className="flex-1 flex overflow-hidden relative min-h-0">
        {/* Fullscreen Mode: Docked Left Sidebar */}
        {isFullscreen && isHistoryOpen && (
          <ChatHistorySidebar
            sessions={sessions}
            activeSessionId={activeSessionId}
            onSelectSession={handleSelectSession}
            onNewChat={handleNewChat}
            onDeleteSession={handleDeleteSession}
            onDeleteDate={handleDeleteDate}
            onClearAll={handleClearAllSessions}
            onClose={() => setIsHistoryOpen(false)}
            isOverlay={false}
            isLoading={isHistoryLoading}
          />
        )}

        {/* Half-Screen Mode: Slide-Over Overlay Sidebar */}
        {!isFullscreen && isHistoryOpen && (
          <div className="absolute inset-0 z-30 flex">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-2xs transition-opacity"
              onClick={() => setIsHistoryOpen(false)}
            />
            {/* Sliding Panel */}
            <div className="relative z-10 h-full animate-in slide-in-from-left duration-200">
              <ChatHistorySidebar
                sessions={sessions}
                activeSessionId={activeSessionId}
                onSelectSession={handleSelectSession}
                onNewChat={handleNewChat}
                onDeleteSession={handleDeleteSession}
                onDeleteDate={handleDeleteDate}
                onClearAll={handleClearAllSessions}
                onClose={() => setIsHistoryOpen(false)}
                isOverlay={true}
                isLoading={isHistoryLoading}
              />
            </div>
          </div>
        )}

        {/* Right Conversation Column */}
        <div className="flex-1 flex flex-col min-w-0 bg-surface-light dark:bg-slate-950 overflow-hidden">
          {/* Messages Thread */}
          <div
            ref={chatBodyRef}
            onScroll={handleScroll}
            onClick={(e) => {
              const link = e.target.closest("a");
              if (link && link.getAttribute("href")) {
                const href = link.getAttribute("href");
                if (!href.startsWith("http") || href.includes(window.location.host)) {
                  onClose();
                }
              }
            }}
            className="flex-1 overflow-y-auto"
          >
            <div
              className={`transition-all ${isFullscreen ? "max-w-4xl mx-auto px-6 py-8 space-y-6" : "p-4 space-y-4"
                }`}
            >
              {messages.length === 0 ? (
                <div className={`text-center ${isFullscreen ? "py-16 max-w-xl mx-auto" : "py-6"}`}>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-100/60 text-primary-600 dark:bg-primary-950/40 dark:text-primary-400 mb-3 shadow-sm ring-4 ring-primary-500/10">
                    <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                    AI Assistant for Metal Operations
                  </h4>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto leading-relaxed">
                    Ask natural language questions to inspect inventory balances, track maritime shipments, view supplier bills, and analyze aging.
                  </p>

                  <div className="mt-6 text-left">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2.5">
                      Suggested Quick Inquiries
                    </p>
                    <div className={isFullscreen ? "grid grid-cols-2 gap-2.5" : "flex flex-col gap-2"}>
                      {INITIAL_SUGGESTIONS.map((prompt, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSend(prompt)}
                          className="flex items-center justify-between rounded-xl border border-slate-200/90 bg-white p-3 text-left text-xs font-medium text-slate-700 shadow-sm transition hover:border-primary-500 hover:bg-primary-50/50 hover:text-primary-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-primary-500/50 dark:hover:bg-primary-950/30 dark:hover:text-primary-300 group cursor-pointer"
                        >
                          <span className="line-clamp-2">{prompt}</span>
                          <span className="text-slate-400 transition-transform group-hover:translate-x-1 shrink-0 ml-2">
                            →
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                messages.map((m, idx) => (
                  <div
                    key={idx}
                    className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}
                  >
                    <div
                      className={`rounded-2xl px-4 py-3 leading-relaxed shadow-xs transition-all ${isFullscreen ? "text-[13px]" : "text-xs"
                        } ${m.role === "user"
                          ? `${isFullscreen ? "max-w-[75%]" : "max-w-[85%]"} bg-gradient-to-r from-primary-600 via-primary-500 to-amber-600 text-white rounded-br-none shadow-primary-500/20`
                          : m.isError
                            ? `${isFullscreen ? "max-w-[85%]" : "max-w-[92%]"} bg-rose-50 text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:border-rose-900 dark:text-rose-200 rounded-bl-none`
                            : m.isStopped
                              ? `${isFullscreen ? "max-w-[85%]" : "max-w-[92%]"} bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-800/60 dark:border-slate-700 dark:text-slate-300 rounded-bl-none italic`
                              : "w-full max-w-[98%] bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100 rounded-bl-none border border-slate-200/90 dark:border-slate-800 shadow-xs"
                        }`}
                    >
                      {m.role === "user" ? (
                        <div className="whitespace-pre-wrap font-medium">{m.content}</div>
                      ) : m.isError ? (
                        <div className="whitespace-pre-wrap">{m.content}</div>
                      ) : m.isStopped ? (
                        <div className="flex items-center gap-2">
                          <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-slate-300 dark:bg-slate-600 text-[9px] font-bold text-slate-700 dark:text-slate-200">
                            ■
                          </span>
                          <span>{m.content}</span>
                        </div>
                      ) : (
                        <ChatMessageContent content={m.content} onNavigate={onClose} />
                      )}
                    </div>

                    {/* Render dynamic structured cards if attached */}
                    {m.structuredContent && (
                      <div className="w-full max-w-[95%]">
                        <ChatCardRenderer content={m.structuredContent} onNavigate={onClose} />
                      </div>
                    )}

                    <span className="mt-1 text-[9px] text-slate-400 px-1">
                      {new Date(m.timestamp || Date.now()).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                ))
              )}

              {isLoading && (
                <div className="flex items-start gap-2">
                  <ChatThinkingIndicator
                    query={currentQuery || messages[messages.length - 1]?.content || ""}
                  />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Suggested Follow-Ups Pills */}
          {messages.length > 0 && suggestedPrompts.length > 0 && !isLoading && (
            <div className="border-t border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
              <div className={`transition-all ${isFullscreen ? "max-w-4xl mx-auto px-6 py-2" : "px-4 py-2"}`}>
                <div
                  ref={suggestionsContainerRef}
                  onWheel={(e) => {
                    if (e.deltaY !== 0) {
                      e.currentTarget.scrollLeft += e.deltaY;
                    }
                  }}
                  className="flex gap-2 overflow-x-auto pb-1.5 pt-0.5 slim-scrollbar"
                >
                  {suggestedPrompts.map((p, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleSend(p)}
                      className="shrink-0 whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-medium text-slate-700 shadow-xs transition hover:border-primary-500 hover:bg-primary-50 hover:text-primary-700 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-primary-400 dark:hover:text-primary-300 active:scale-95 cursor-pointer"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Input Form */}
          <div
            onClick={(e) => {
              if (!e.target.closest("button")) {
                textareaRef.current?.focus();
              }
            }}
            className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 cursor-text select-none shrink-0"
          >
            <div className={`transition-all ${isFullscreen ? "max-w-4xl mx-auto px-6 py-3.5" : "p-3.5"}`}>
              <div
                onClick={(e) => {
                  if (!e.target.closest("button")) {
                    textareaRef.current?.focus();
                  }
                }}
                className="flex items-end gap-2 rounded-xl border border-slate-200 bg-slate-50/80 p-2.5 cursor-text transition-all focus-within:border-primary-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-500/20 dark:border-slate-800 dark:bg-slate-800/50 dark:focus-within:border-primary-500 dark:focus-within:bg-slate-900 shadow-2xs"
              >
                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    adjustTextareaHeight();
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    isLoading
                      ? "Type a new prompt or click Stop..."
                      : "Ask about stock, shipments, suppliers, invoices..."
                  }
                  className={`flex-1 resize-none bg-transparent px-2 py-1 text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white slim-scrollbar cursor-text leading-relaxed ${isFullscreen ? "text-sm" : "text-xs"
                    }`}
                  style={{ minHeight: isFullscreen ? "30px" : "28px" }}
                />

                <div className="flex items-center gap-1.5 shrink-0">
                  {isLoading && (
                    <button
                      type="button"
                      onClick={handleStop}
                      title="Stop current generation"
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500 text-white shadow-md shadow-rose-500/25 transition hover:bg-rose-600 active:scale-95 animate-pulse cursor-pointer"
                    >
                      <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                        <rect x="5" y="5" width="14" height="14" rx="2" />
                      </svg>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => handleSend()}
                    disabled={!input.trim()}
                    title={isLoading ? "Send new prompt (stops current)" : "Send prompt"}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg text-white shadow-md transition active:scale-95 cursor-pointer ${!input.trim()
                      ? "bg-slate-300 dark:bg-slate-700 cursor-not-allowed opacity-40"
                      : "bg-primary-500 hover:bg-primary-600 shadow-primary-500/20"
                      }`}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="mt-1.5 text-center text-[10px] text-slate-400 dark:text-slate-500">
                Verified Double-Entry Ledger System
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
